// Version Management and Data Migration System
import Storage from './storageES.js';

const VERSION_KEY = 'app_version';
const DATA_BACKUP_KEY = 'data_backup';
const MIGRATION_HISTORY_KEY = 'migration_history';

// Current app version - update this when you make changes
export const CURRENT_VERSION = '1.6.0';

// Version history and migration info
export const VERSION_HISTORY = {
  '1.3.0': {
    date: '2025-01-15',
    changes: ['Initial PWA implementation', 'Basic travel features'],
    requiresMigration: false
  },
  '1.4.0': {
    date: '2025-01-16',
    changes: ['PWA installation fixes', 'Version management system', 'Data preservation'],
    requiresMigration: false
  },
  '1.5.0': {
    date: '2025-01-17',
    changes: ['Enhanced update system with progress bar', 'Improved version display in footer', 'Better update notifications'],
    requiresMigration: false
  },
  '1.6.0': {
    date: '2025-01-18',
    changes: ['🔐 Advanced Authentication System', '📧 Email registration with GDPR compliance', '🎫 NFC Premium licensing for merchandise', '👆 Biometric & PIN security options', '📄 Document security features'],
    requiresMigration: false
  }
};

export const versionManager = {

  // Get current app version
  getCurrentVersion() {
    return CURRENT_VERSION;
  },

  // Get stored version (what user has installed)
  getStoredVersion() {
    return localStorage.getItem(VERSION_KEY) || '1.0.0';
  },

  // Check if app needs update
  needsUpdate() {
    const storedVersion = this.getStoredVersion();
    const currentVersion = this.getCurrentVersion();
    return this.compareVersions(currentVersion, storedVersion) > 0;
  },

  // Compare version strings (returns 1 if v1 > v2, -1 if v1 < v2, 0 if equal)
  compareVersions(v1, v2) {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;

      if (part1 > part2) return 1;
      if (part1 < part2) return -1;
    }
    return 0;
  },

  // Create backup of all user data before update
  async createDataBackup() {
    try {
      console.log('[Version] Creating data backup...');

      const userData = {
        version: this.getStoredVersion(),
        timestamp: Date.now(),
        userSettings: Storage.getSettings(),
        tripData: Storage.getTripData(),
        budgetData: Storage.getBudgetData(),
        // Backup all localStorage data
        allData: { ...localStorage }
      };

      // Store backup with timestamp
      const backupKey = `${DATA_BACKUP_KEY}_${Date.now()}`;
      localStorage.setItem(backupKey, JSON.stringify(userData));

      // Keep only last 3 backups to save space
      this.cleanupOldBackups();

      console.log('✅ Data backup created successfully');
      return backupKey;
    } catch (error) {
      console.error('❌ Failed to create data backup:', error);
      throw error;
    }
  },

  // Clean up old backups (keep only 3 most recent)
  cleanupOldBackups() {
    const keys = Object.keys(localStorage).filter(key => key.startsWith(DATA_BACKUP_KEY));
    if (keys.length > 3) {
      // Sort by timestamp (newest first)
      keys.sort((a, b) => {
        const timestampA = parseInt(a.split('_').pop());
        const timestampB = parseInt(b.split('_').pop());
        return timestampB - timestampA;
      });

      // Remove old backups
      keys.slice(3).forEach(key => {
        localStorage.removeItem(key);
        console.log(`[Version] Removed old backup: ${key}`);
      });
    }
  },

  // Perform data migration if needed
  async migrateData(fromVersion, toVersion) {
    console.log(`[Version] Migrating data from ${fromVersion} to ${toVersion}`);

    try {
      // Add migration logic here as needed
      const migrationHistory = JSON.parse(localStorage.getItem(MIGRATION_HISTORY_KEY) || '[]');

      migrationHistory.push({
        from: fromVersion,
        to: toVersion,
        timestamp: Date.now(),
        success: true
      });

      localStorage.setItem(MIGRATION_HISTORY_KEY, JSON.stringify(migrationHistory));
      console.log('✅ Data migration completed successfully');

    } catch (error) {
      console.error('❌ Data migration failed:', error);
      throw error;
    }
  },

  // Update app version and handle data preservation
  async updateVersion() {
    const oldVersion = this.getStoredVersion();
    const newVersion = this.getCurrentVersion();

    try {
      console.log(`[Version] Updating from ${oldVersion} to ${newVersion}`);

      // 1. Create backup before any changes
      await this.createDataBackup();

      // 2. Perform migration if needed
      if (this.requiresMigration(oldVersion, newVersion)) {
        await this.migrateData(oldVersion, newVersion);
      }

      // 3. Update version
      localStorage.setItem(VERSION_KEY, newVersion);

      // 4. Log update
      console.log(`✅ App updated to version ${newVersion}`);

      // 5. Show success message to user
      this.showUpdateSuccess(oldVersion, newVersion);

    } catch (error) {
      console.error('❌ Version update failed:', error);
      // Restore from backup if possible
      await this.restoreFromBackup();
      throw error;
    }
  },

  // Check if migration is required
  requiresMigration(fromVersion, toVersion) {
    // Check if any version in between requires migration
    for (const version in VERSION_HISTORY) {
      if (this.compareVersions(version, fromVersion) > 0 &&
          this.compareVersions(version, toVersion) <= 0) {
        if (VERSION_HISTORY[version].requiresMigration) {
          return true;
        }
      }
    }
    return false;
  },

  // Restore data from most recent backup
  async restoreFromBackup() {
    try {
      console.log('[Version] Attempting to restore from backup...');

      const backupKeys = Object.keys(localStorage)
        .filter(key => key.startsWith(DATA_BACKUP_KEY))
        .sort((a, b) => {
          const timestampA = parseInt(a.split('_').pop());
          const timestampB = parseInt(b.split('_').pop());
          return timestampB - timestampA;
        });

      if (backupKeys.length === 0) {
        console.warn('No backup found');
        return false;
      }

      const latestBackup = localStorage.getItem(backupKeys[0]);
      const backupData = JSON.parse(latestBackup);

      // Restore user data
      if (backupData.userSettings) {
        Storage.saveSettings(backupData.userSettings);
      }
      if (backupData.tripData) {
        Storage.saveTripData(backupData.tripData);
      }
      if (backupData.budgetData) {
        Storage.saveBudgetData(backupData.budgetData);
      }

      console.log('✅ Data restored from backup successfully');
      return true;

    } catch (error) {
      console.error('❌ Failed to restore from backup:', error);
      return false;
    }
  },

  // Show update success message
  showUpdateSuccess(oldVersion, newVersion) {
    const changes = VERSION_HISTORY[newVersion]?.changes || ['Bug fixes and improvements'];

    // Dispatch custom event for UI to handle
    window.dispatchEvent(new CustomEvent('app-updated', {
      detail: {
        oldVersion,
        newVersion,
        changes
      }
    }));
  },

  // Get version info for display
  getVersionInfo() {
    const currentVersion = this.getCurrentVersion();
    const versionInfo = VERSION_HISTORY[currentVersion];
    const needsUpdate = this.needsUpdate();

    console.log('[VersionManager] getVersionInfo - current:', currentVersion, 'needsUpdate:', needsUpdate);

    return {
      version: currentVersion,
      date: versionInfo?.date || new Date().toISOString().split('T')[0],
      changes: versionInfo?.changes || ['Current version'],
      needsUpdate: needsUpdate
    };
  },

  // Initialize version system
  async initialize() {
    try {
      console.log('[Version] Initializing version management...');

      const storedVersion = this.getStoredVersion();
      const currentVersion = this.getCurrentVersion();

      console.log(`[Version] Stored: ${storedVersion}, Current: ${currentVersion}`);

      // If this is first run, just set the version
      if (storedVersion === '1.0.0' && localStorage.length === 0) {
        localStorage.setItem(VERSION_KEY, currentVersion);
        console.log('[Version] First run - version set to', currentVersion);
        return;
      }

      // Force update stored version to current on app load (production fix)
      // This ensures the stored version matches the deployed version
      if (this.needsUpdate()) {
        console.log('[Version] Auto-updating stored version to match deployed version');
        localStorage.setItem(VERSION_KEY, currentVersion);

        // Dispatch event to notify components
        window.dispatchEvent(new CustomEvent('version-updated'));
      } else {
        console.log('[Version] App is up to date');
      }

    } catch (error) {
      console.error('[Version] Initialization failed:', error);
    }
  }
};

export default versionManager;